/**
 * Schedule editor — single modal covering everything an operator needs to
 * change about a service-plan schedule: its own metadata (name / interval /
 * unit / recurring) AND the ordered checklist a technician works through
 * during the service. Two formerly-separate dialogs collapsed into one tab-
 * free flow because they're conceptually two views of the same thing.
 *
 * Drag-and-drop reorder uses native HTML5 drag — no extra dep. On drop the
 * affected items get fresh sortOrder values (10, 20, 30, …) so future
 * inserts have room without renumbering.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GripVertical, Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';

interface Item {
  id: string;
  task: string | null;
  groupHeader: string | null;
  sortOrder: number | null;
}
interface Checklist {
  id: string;
  servicePlan?: { id: string; name?: string } | string | null;
  servicePlanSchedule?: { id: string; name?: string } | string | null;
}

export interface ChecklistEditorDialogProps {
  open: boolean;
  onClose: () => void;
  scheduleId: string;
  scheduleName?: string;
  servicePlanId?: string;
}

export function ChecklistEditorDialog({
  open, onClose, scheduleId, scheduleName, servicePlanId,
}: ChecklistEditorDialogProps) {
  const qc = useQueryClient();

  // -------- Schedule metadata (name / interval / unit / recurring) --------
  // Same dialog edits the schedule itself, so we fetch fresh values on open.
  // Local form state mirrors the server values until the user clicks Save.
  const scheduleQuery = useQuery({
    queryKey: ['checklist-editor', 'schedule', scheduleId],
    queryFn: async () => {
      const r = await api<{
        id: string; name?: string;
        serviceInterval?: number | null; unitOfMeasurement?: string | null;
        recurring?: boolean; isOilChange?: boolean;
        // The /servicing/schedules route populates `includes` with {id, name},
        // but legacy callers may still see bare ObjectId strings — handle both.
        includes?: Array<{ id?: string; _id?: string; name?: string } | string> | null;
        servicePlan?: { id?: string; _id?: string } | string | null;
      }>(`/servicing/schedules/${scheduleId}`);
      return r.data ?? null;
    },
    enabled: open && !!scheduleId,
  });

  // Normalise the populated `includes` array → a list of ids the form binds against.
  const initialIncludeIds = React.useMemo(() => {
    const raw = scheduleQuery.data?.includes ?? [];
    return raw
      .map((x: any) => (typeof x === 'string' ? x : x?.id ?? x?._id ?? null))
      .filter((x: any): x is string => !!x);
  }, [scheduleQuery.data]);

  const [form, setForm] = React.useState<{
    name: string; serviceInterval: string; unitOfMeasurement: string;
    recurring: boolean; isOilChange: boolean; includes: string[];
  }>({ name: '', serviceInterval: '', unitOfMeasurement: '', recurring: false, isOilChange: false, includes: [] });
  // Sync local form state when the server data resolves / re-fetches.
  React.useEffect(() => {
    const s = scheduleQuery.data;
    if (s) {
      setForm({
        name: s.name ?? '',
        serviceInterval: s.serviceInterval != null ? String(s.serviceInterval) : '',
        unitOfMeasurement: s.unitOfMeasurement ?? '',
        recurring: !!s.recurring,
        isOilChange: !!s.isOilChange,
        includes: initialIncludeIds,
      });
    }
  }, [scheduleQuery.data, initialIncludeIds]);

  // Fetch sibling schedules in the same plan so the Include picker can list
  // candidates. Prefer the prop-supplied `servicePlanId` (always present when
  // opened from the plan edit page); fall back to the schedule doc's own
  // servicePlan ref for the standalone-form open path.
  const resolvedPlanId = React.useMemo(() => {
    if (servicePlanId) return servicePlanId;
    const sp = scheduleQuery.data?.servicePlan as any;
    if (!sp) return null;
    if (typeof sp === 'string') return sp;
    return sp.id ?? sp._id ?? null;
  }, [servicePlanId, scheduleQuery.data]);

  const siblingsQuery = useQuery({
    queryKey: ['checklist-editor', 'siblings', resolvedPlanId, scheduleId],
    queryFn: async () => {
      if (!resolvedPlanId) return [] as Array<{ id: string; name: string }>;
      const r = await api<Array<{ id: string; name: string }>>(
        `/servicing/schedules?servicePlan=${encodeURIComponent(resolvedPlanId)}&limit=500&sort=name&order=asc`,
      );
      return (r.data ?? []).filter((s) => s.id !== scheduleId);
    },
    enabled: open && !!resolvedPlanId,
    staleTime: 30_000,
  });
  const siblings = siblingsQuery.data ?? [];

  function toggleInclude(id: string) {
    setForm((f) => ({
      ...f,
      includes: f.includes.includes(id) ? f.includes.filter((x) => x !== id) : [...f.includes, id],
    }));
  }

  const saveSchedule = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        serviceInterval: form.serviceInterval === '' ? null : Number(form.serviceInterval),
        unitOfMeasurement: form.unitOfMeasurement || null,
        recurring: form.recurring,
        // Setting isOilChange explicitly trips the model's isOilChangeAuto
        // flag to false (server-side hook), so the name-heuristic won't
        // overwrite the operator's decision on future edits.
        isOilChange: form.isOilChange,
        includes: form.includes,
      };
      const r = await api(`/servicing/schedules/${scheduleId}`, { method: 'PUT', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Save failed');
      return r.data;
    },
    onSuccess: () => {
      toast('Schedule saved', undefined, 'success');
      qc.invalidateQueries({ queryKey: ['checklist-editor', 'schedule', scheduleId] });
      // Invalidate the parent service plan's schedules related list so the
      // change appears immediately without a manual refresh.
      qc.invalidateQueries({ queryKey: ['related', '/servicing/schedules'] });
    },
    onError: (e: any) => toast('Error', e.message ?? String(e), 'error'),
  });
  const scheduleDirty = React.useMemo(() => {
    const s = scheduleQuery.data;
    if (!s) return false;
    if ((s.name ?? '') !== form.name) return true;
    if (String(s.serviceInterval ?? '') !== form.serviceInterval) return true;
    if ((s.unitOfMeasurement ?? '') !== form.unitOfMeasurement) return true;
    if (!!s.recurring !== form.recurring) return true;
    if (!!s.isOilChange !== form.isOilChange) return true;
    const a = [...initialIncludeIds].sort();
    const b = [...form.includes].sort();
    if (a.length !== b.length || a.some((id, i) => id !== b[i])) return true;
    return false;
  }, [scheduleQuery.data, form, initialIncludeIds]);

  // -------- Checklist (single row joining the schedule to its items) --------
  // Resolve the (single) checklist row for this schedule. Create one on the
  // fly when missing so the operator never has to do a separate "+ New"
  // dance — the checklist itself is just a join row, not real data.
  const clQuery = useQuery({
    queryKey: ['checklist-editor', 'checklist', scheduleId],
    queryFn: async () => {
      const r = await api<Checklist[]>(
        `/service-plan-schedule-checklists?servicePlanSchedule=${encodeURIComponent(scheduleId)}&limit=1`,
      );
      return (r.data ?? [])[0] ?? null;
    },
    enabled: open && !!scheduleId,
    staleTime: 30_000,
  });
  const createChecklist = useMutation({
    mutationFn: async () => {
      const r = await api<Checklist>('/service-plan-schedule-checklists', {
        method: 'POST',
        body: JSON.stringify({
          servicePlan: servicePlanId ?? null,
          servicePlanSchedule: scheduleId,
        }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed to create checklist');
      return r.data!;
    },
    onSuccess: (created) => {
      // Silent — the parent row is internal plumbing, not user-visible
      // data. No toast or operators will get spammed every time they
      // open a schedule that didn't have a checklist yet.
      qc.setQueryData(['checklist-editor', 'checklist', scheduleId], created);
    },
    onError: (e: any) => toast('Error', e.message ?? String(e), 'error'),
  });

  const checklist = clQuery.data ?? null;
  const checklistId = checklist?.id ?? null;

  // Auto-create the checklist parent row on first open if it's missing.
  // The parent is an invisible join between the schedule and its items —
  // making the operator click an explicit "Create Checklist" button just
  // to bring up an empty items list was confusing UX. Now they land
  // directly on "No items yet — add one below" with the Add Item control
  // available immediately. Guarded so we don't fire it repeatedly while
  // the mutation is in-flight or if the dialog re-opens.
  React.useEffect(() => {
    if (!open) return;
    if (clQuery.isLoading || clQuery.isFetching) return;
    if (checklist) return;
    if (createChecklist.isPending || createChecklist.isSuccess) return;
    createChecklist.mutate();
    // Intentionally omit `createChecklist` from deps — including it would
    // re-fire as soon as the mutation flips state mid-cycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clQuery.isLoading, clQuery.isFetching, checklist]);

  // Items — sorted by sortOrder ascending, with nulls last. Cached
  // optimistically so drag-reorder feels instant.
  const itemsQuery = useQuery({
    queryKey: ['checklist-editor', 'items', checklistId],
    queryFn: async () => {
      const r = await api<Item[]>(
        `/service-plan-schedule-checklist-items?parent=${encodeURIComponent(checklistId!)}&limit=500`,
      );
      const list = r.data ?? [];
      list.sort((a, b) => (a.sortOrder ?? Number.POSITIVE_INFINITY) - (b.sortOrder ?? Number.POSITIVE_INFINITY));
      return list;
    },
    enabled: open && !!checklistId,
  });
  const items = itemsQuery.data ?? [];

  // Cache key for the items list — used by every optimistic update below.
  const itemsKey = ['checklist-editor', 'items', checklistId];

  const createItem = useMutation({
    mutationFn: async (payload: { task: string; groupHeader?: string; sortOrder: number }) => {
      const r = await api<Item>('/service-plan-schedule-checklist-items', {
        method: 'POST',
        body: JSON.stringify({ parent: checklistId, ...payload }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data!;
    },
    // Optimistic insert — stub row appears in the list immediately so the
    // operator sees the action land without waiting for the round-trip.
    // The temp id is replaced by the real one on the post-settle refetch.
    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: itemsKey });
      const prev = qc.getQueryData<Item[]>(itemsKey);
      const tempId = `__tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const stub: Item = {
        id: tempId,
        task: payload.task,
        groupHeader: payload.groupHeader ?? null,
        sortOrder: payload.sortOrder,
      };
      qc.setQueryData<Item[]>(itemsKey, (old) => {
        const next = [...(old ?? []), stub];
        next.sort((a, b) => (a.sortOrder ?? Number.POSITIVE_INFINITY) - (b.sortOrder ?? Number.POSITIVE_INFINITY));
        return next;
      });
      return { prev };
    },
    onError: (e: any, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(itemsKey, ctx.prev);
      toast('Error', e.message ?? String(e), 'error');
    },
    // Refetch once the request settles (success OR failure) so the real
    // server-issued id replaces the temp + any server-side reordering
    // takes effect.
    onSettled: () => qc.invalidateQueries({ queryKey: itemsKey }),
  });
  const updateItem = useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; task?: string; groupHeader?: string | null; sortOrder?: number }) => {
      const r = await api<Item>(`/service-plan-schedule-checklist-items/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data!;
    },
    // Optimistic patch — the edited fields apply locally immediately.
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: itemsKey });
      const prev = qc.getQueryData<Item[]>(itemsKey);
      qc.setQueryData<Item[]>(itemsKey, (old) =>
        (old ?? []).map((row) => (row.id === vars.id ? { ...row, ...vars } : row)),
      );
      return { prev };
    },
    onError: (e: any, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(itemsKey, ctx.prev);
      toast('Error', e.message ?? String(e), 'error');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: itemsKey }),
  });
  // Per-id in-flight set so the trash icon can disable itself while the
  // DELETE is on the wire. With the optimistic remove below the row
  // vanishes immediately, but the disabled state still helps if the row
  // gets re-added by a rollback before the user can react.
  const [pendingDeleteIds, setPendingDeleteIds] = React.useState<Set<string>>(new Set());
  const deleteItem = useMutation({
    mutationFn: async (id: string) => {
      setPendingDeleteIds((s) => { const n = new Set(s); n.add(id); return n; });
      try {
        const r = await api(`/service-plan-schedule-checklist-items/${id}`, { method: 'DELETE' });
        // Treat "not found" as idempotent success. A fast double-click
        // on the trash icon sends two DELETE requests for the same id;
        // the second arrives after the first has removed the row and
        // would otherwise toast "Record not found". The end state the
        // caller asked for (row gone) is already true, so no error.
        if (!r.success) {
          const msg = r.error?.message ?? '';
          const code = (r.error as any)?.code ?? '';
          const isMissing = code === 'NOT_FOUND' || /not\s*found/i.test(msg);
          if (!isMissing) throw new Error(msg || 'Failed');
        }
      } finally {
        setPendingDeleteIds((s) => { const n = new Set(s); n.delete(id); return n; });
      }
    },
    // Optimistic remove — the row disappears from the list immediately;
    // the server DELETE catches up in the background.
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: itemsKey });
      const prev = qc.getQueryData<Item[]>(itemsKey);
      qc.setQueryData<Item[]>(itemsKey, (old) => (old ?? []).filter((row) => row.id !== id));
      return { prev };
    },
    onError: (e: any, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(itemsKey, ctx.prev);
      toast('Error', e.message ?? String(e), 'error');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: itemsKey }),
  });

  // ----- New-item composer state -----
  const [composerOpen, setComposerOpen] = React.useState(false);
  // Search filter over the library list. Empty = show all.
  const [librarySearchText, setLibrarySearchText] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(librarySearchText), 180);
    return () => clearTimeout(t);
  }, [librarySearchText]);

  // "+ Create new task" mode within the composer — a separate sub-form.
  const [creatingNew, setCreatingNew] = React.useState(false);
  const [newTask, setNewTask] = React.useState('');
  const [newGroup, setNewGroup] = React.useState('');

  // Fetch the library — defaults to first 100 sorted alphabetically by
  // `listItem`. Server-side search applies the filter when text is typed.
  const librarySearch = useQuery({
    queryKey: ['checklist-editor', 'library', debouncedSearch],
    queryFn: async () => {
      const qs = new URLSearchParams({ limit: '100', sort: 'listItem', order: 'asc' });
      if (debouncedSearch.trim()) qs.set('search', debouncedSearch.trim());
      const r = await api<Array<{ id: string; listItem: string; checklistGroup?: { id: string; groupName?: string } | null }>>(
        `/checklists?${qs.toString()}`,
      );
      return r.data ?? [];
    },
    enabled: composerOpen && open,
    staleTime: 30_000,
  });
  const libraryItems = librarySearch.data ?? [];

  // Skip library rows that already exist on this schedule (no point picking
  // the same task twice). Match by listItem text (case-insensitive) since
  // schedule items snapshot the text rather than referencing the library id.
  const alreadyAdded = new Set(items.map((it) => (it.task ?? '').trim().toLowerCase()));
  const availableLibrary = libraryItems.filter((l) => !alreadyAdded.has((l.listItem ?? '').trim().toLowerCase()));

  // Add to /checklists library (so the task is reusable elsewhere), then
  // append it onto this schedule's list. POSTed sequentially so the
  // schedule item references a real library row.
  const createLibraryItem = useMutation({
    mutationFn: async (payload: { listItem: string }) => {
      const r = await api<{ id: string; listItem: string }>('/checklists', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed to add to library');
      return r.data!;
    },
    onError: (e: any) => toast('Error', e.message ?? String(e), 'error'),
  });

  function nextSortOrder() {
    const last = items.reduce((m, it) => Math.max(m, it.sortOrder ?? 0), 0);
    return last + 10;
  }

  async function handleAddFromLibrary(lib: { id: string; listItem: string; checklistGroup?: { groupName?: string } | null }) {
    // Snapshot library text + group onto the schedule item.
    await createItem.mutateAsync({
      task: lib.listItem,
      groupHeader: lib.checklistGroup?.groupName || undefined,
      sortOrder: nextSortOrder(),
    });
  }

  async function handleAddNew() {
    const text = newTask.trim();
    if (!text) return;
    // 1. Add to the library so it's reusable on future services. Swallow
    //    unique-listItem collisions — the user's intent is clear either way.
    try { await createLibraryItem.mutateAsync({ listItem: text }); } catch { /* duplicate */ }
    // 2. Snapshot onto this schedule.
    await createItem.mutateAsync({
      task: text,
      groupHeader: newGroup.trim() || undefined,
      sortOrder: nextSortOrder(),
    });
    setNewTask(''); setNewGroup(''); setCreatingNew(false);
  }

  // ----- Per-row edit state -----
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editTask, setEditTask] = React.useState('');
  const [editGroup, setEditGroup] = React.useState('');
  function startEdit(it: Item) {
    setEditingId(it.id);
    setEditTask(it.task ?? '');
    setEditGroup(it.groupHeader ?? '');
  }
  function saveEdit() {
    if (!editingId) return;
    updateItem.mutate({ id: editingId, task: editTask.trim(), groupHeader: editGroup.trim() || null });
    setEditingId(null);
  }

  // ----- Drag-and-drop reorder -----
  const [dragId, setDragId] = React.useState<string | null>(null);
  const [overId, setOverId] = React.useState<string | null>(null);
  function handleDragStart(id: string) { setDragId(id); }
  function handleDragOver(e: React.DragEvent, id: string) {
    e.preventDefault();
    if (dragId && dragId !== id) setOverId(id);
  }
  function handleDrop(targetId: string) {
    const fromIdx = items.findIndex((it) => it.id === dragId);
    const toIdx = items.findIndex((it) => it.id === targetId);
    setDragId(null); setOverId(null);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const next = [...items];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    // Renumber sortOrder = (i + 1) * 10 so the visual order is the saved order.
    // Persist by firing parallel PUTs; cache invalidates on completion.
    next.forEach((it, i) => {
      const want = (i + 1) * 10;
      if (it.sortOrder !== want) {
        updateItem.mutate({ id: it.id, sortOrder: want });
      }
    });
    // Update the cache optimistically so the row reorders before the saves land.
    qc.setQueryData(['checklist-editor', 'items', checklistId],
      next.map((it, i) => ({ ...it, sortOrder: (i + 1) * 10 })));
  }

  // Group items under their `groupHeader` for display, preserving the
  // overall sortOrder (group headers sort with the items inside them).
  const groups: Array<{ name: string | null; items: Item[] }> = [];
  for (const it of items) {
    const g = it.groupHeader || null;
    const last = groups[groups.length - 1];
    if (last && last.name === g) last.items.push(it);
    else groups.push({ name: g, items: [it] });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            Edit Schedule{scheduleName ? ` — ${scheduleName}` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-6">
          {/* ---- Schedule metadata ---- */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground border-b pb-2">
              Schedule details
            </h3>
            {scheduleQuery.isLoading ? (
              <div className="text-sm text-muted-foreground py-4 text-center">Loading schedule…</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                <div className="md:col-span-2">
                  <Label className="text-xs">Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. A-Service"
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Service Interval</Label>
                  <Input
                    type="number"
                    value={form.serviceInterval}
                    onChange={(e) => setForm((f) => ({ ...f, serviceInterval: e.target.value }))}
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Unit of Measurement</Label>
                  <Select value={form.unitOfMeasurement || '__none__'} onValueChange={(v) => setForm((f) => ({ ...f, unitOfMeasurement: v === '__none__' ? '' : v }))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      <SelectItem value="Distance (km)">Distance (km)</SelectItem>
                      <SelectItem value="Hours of Operation">Hours of Operation</SelectItem>
                      <SelectItem value="Days">Days</SelectItem>
                      <SelectItem value="Months">Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Checkbox
                    checked={form.recurring}
                    onCheckedChange={(c) => setForm((f) => ({ ...f, recurring: !!c }))}
                  />
                  <Label className="text-sm">Recurring</Label>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Checkbox
                    checked={form.isOilChange}
                    onCheckedChange={(c) => setForm((f) => ({ ...f, isOilChange: !!c }))}
                  />
                  <Label className="text-sm">Engine Oil Change</Label>
                </div>
                {/* Include picker — other schedules in the same plan that
                    completing this one also satisfies. Only sibling schedules
                    on the same plan are listable. Server-side cycle + self-
                    include validation backstops the UI. */}
                <div className="md:col-span-2">
                  <Label className="text-xs">Include</Label>
                  <p className="text-[10px] text-muted-foreground mb-1">
                    Other schedules that completing this one also satisfies (e.g. B includes A).
                  </p>
                  {!resolvedPlanId ? (
                    <div className="text-xs text-muted-foreground border rounded-md px-2 py-1.5">
                      Assign a service plan first to pick siblings.
                    </div>
                  ) : siblingsQuery.isLoading ? (
                    <div className="text-xs text-muted-foreground border rounded-md px-2 py-1.5">Loading…</div>
                  ) : siblings.length === 0 ? (
                    <div className="text-xs text-muted-foreground border rounded-md px-2 py-1.5">
                      No other schedules on this plan yet.
                    </div>
                  ) : (
                    <div className="rounded-md border bg-background max-h-40 overflow-y-auto">
                      {siblings.map((s) => (
                        <label
                          key={s.id}
                          className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-accent cursor-pointer"
                        >
                          <Checkbox
                            checked={form.includes.includes(s.id)}
                            onCheckedChange={() => toggleInclude(s.id)}
                          />
                          <span>{s.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <div className="md:col-span-2 flex justify-end">
                  <Button
                    size="sm"
                    onClick={() => saveSchedule.mutate()}
                    disabled={!scheduleDirty || saveSchedule.isPending || !form.name.trim()}
                  >
                    {saveSchedule.isPending ? 'Saving…' : 'Save Schedule'}
                  </Button>
                </div>
              </div>
            )}
          </section>

          {/* ---- Checklist ---- */}
          <section>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground border-b pb-2 mb-3">
              Checklist items
            </h3>
          {(clQuery.isLoading || (!checklist && createChecklist.isPending)) && (
            <div className="text-sm text-muted-foreground py-6 text-center">Loading checklist…</div>
          )}

          {checklist && (
            <>
              {itemsQuery.isLoading && (
                <div className="text-sm text-muted-foreground py-6 text-center">Loading items…</div>
              )}
              {!itemsQuery.isLoading && items.length === 0 && (
                <div className="text-sm text-muted-foreground py-4 text-center">No items yet — add one below.</div>
              )}

              <div className="space-y-3">
                {groups.map((g, gi) => (
                  <div key={`${g.name ?? '__nogroup__'}-${gi}`}>
                    {g.name && (
                      <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mt-2 mb-1 px-1">
                        {g.name}
                      </div>
                    )}
                    <ul className="space-y-1">
                      {g.items.map((it) => {
                        const isEditing = editingId === it.id;
                        const isOver = overId === it.id && dragId !== it.id;
                        return (
                          <li
                            key={it.id}
                            draggable={!isEditing}
                            onDragStart={() => handleDragStart(it.id)}
                            onDragOver={(e) => handleDragOver(e, it.id)}
                            onDrop={() => handleDrop(it.id)}
                            onDragEnd={() => { setDragId(null); setOverId(null); }}
                            className={
                              'flex items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-sm '
                              + (isOver ? 'border-primary bg-accent' : '')
                              + (dragId === it.id ? ' opacity-60' : '')
                            }
                          >
                            <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab flex-shrink-0" />
                            {isEditing ? (
                              <>
                                <Input
                                  value={editTask}
                                  onChange={(e) => setEditTask(e.target.value)}
                                  placeholder="Task"
                                  className="h-7 text-xs flex-1"
                                  autoFocus
                                />
                                <Input
                                  value={editGroup}
                                  onChange={(e) => setEditGroup(e.target.value)}
                                  placeholder="Group (optional)"
                                  className="h-7 text-xs w-36"
                                />
                                <Button size="icon" variant="ghost" onClick={saveEdit} title="Save">
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" onClick={() => setEditingId(null)} title="Cancel">
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <span className="flex-1 truncate">{it.task || <em className="text-muted-foreground">(no task)</em>}</span>
                                <Button size="icon" variant="ghost" onClick={() => startEdit(it)} title="Edit">
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => deleteItem.mutate(it.id)}
                                  // Disable while THIS row's delete is in flight so a
                                  // fast second click can't fire a duplicate DELETE
                                  // (the source of the "record not found" toast).
                                  disabled={pendingDeleteIds.has(it.id)}
                                  title="Delete"
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>

              {/* ---- Library-first composer -----------------------------------
                  Opens to a browsable list of the 300+ reusable tasks. Click
                  to add. Search input filters live. A "+ Create new" toggle
                  reveals an inline form for tasks that don't exist yet —
                  saved to the library AND added to this schedule.
              */}
              <div className="border-t mt-4 pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Add new item</Label>
                  {!composerOpen && (
                    <Button size="sm" variant="outline" onClick={() => setComposerOpen(true)}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add item
                    </Button>
                  )}
                </div>

                {composerOpen && (
                  <div className="rounded-md border bg-muted/30 p-3 space-y-3">
                    {!creatingNew ? (
                      <>
                        <div className="flex gap-2 items-center">
                          <Input
                            value={librarySearchText}
                            onChange={(e) => setLibrarySearchText(e.target.value)}
                            placeholder="Search library…"
                            className="h-8 text-sm flex-1"
                            autoFocus
                          />
                          <Button size="sm" variant="outline" onClick={() => setCreatingNew(true)}>
                            <Plus className="h-3.5 w-3.5 mr-1" /> Create new
                          </Button>
                        </div>

                        <div className="rounded-md border bg-background max-h-64 overflow-y-auto">
                          {librarySearch.isLoading && (
                            <div className="px-3 py-2 text-xs text-muted-foreground">Loading library…</div>
                          )}
                          {!librarySearch.isLoading && availableLibrary.length === 0 && (
                            <div className="px-3 py-2 text-xs text-muted-foreground">
                              {librarySearchText.trim()
                                ? 'No matching tasks in the library.'
                                : 'Every library task is already on this schedule.'}
                            </div>
                          )}
                          {availableLibrary.map((lib) => (
                            <button
                              key={lib.id}
                              type="button"
                              onClick={() => handleAddFromLibrary(lib)}
                              disabled={createItem.isPending}
                              className="block w-full text-left px-3 py-1.5 text-xs border-b last:border-0 hover:bg-accent hover:text-accent-foreground transition-colors"
                            >
                              <span className="font-medium">{lib.listItem}</span>
                              {lib.checklistGroup?.groupName && (
                                <span className="ml-2 text-muted-foreground">· {lib.checklistGroup.groupName}</span>
                              )}
                            </button>
                          ))}
                        </div>

                        <div className="flex justify-end">
                          <Button size="sm" variant="ghost" onClick={() => { setComposerOpen(false); setLibrarySearchText(''); }}>
                            Done
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <Label className="text-xs">Task</Label>
                          <Input
                            value={newTask}
                            onChange={(e) => setNewTask(e.target.value)}
                            placeholder="e.g. Check brake fluid level"
                            className="h-8 text-sm"
                            autoFocus
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Group (optional)</Label>
                          <Input
                            value={newGroup}
                            onChange={(e) => setNewGroup(e.target.value)}
                            placeholder="e.g. Engine Bay, Cab Interior, Tyres"
                            className="h-8 text-sm"
                          />
                          <p className="text-[10px] text-muted-foreground mt-1">
                            Group is a section heading the technician sees above this task on the
                            checklist — clusters related tasks (e.g. all "Engine Bay" items show under
                            one heading). Leave blank for ungrouped items.
                          </p>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => { setCreatingNew(false); setNewTask(''); setNewGroup(''); }}>
                            Back
                          </Button>
                          <Button size="sm" onClick={handleAddNew}
                            disabled={!newTask.trim() || createItem.isPending || createLibraryItem.isPending}>
                            <Plus className="h-3.5 w-3.5 mr-1" />
                            {createItem.isPending || createLibraryItem.isPending ? 'Adding…' : 'Add to schedule & library'}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          </section>
        </div>

        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
